'use client';

import { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { confirmSubscription, startSubscription } from '@/app/actions/subscriptions';
import { callAction } from '@/lib/call-action';
import type { SubscriptionPlanDto } from '@peach/types/subscriptions/subscription.dto.js';
import { planLine, planName, planTotal } from './plan-copy';

const EmbeddedCheckoutPanel = dynamic(() => import('@/components/payment/EmbeddedCheckoutPanel'), {
  ssr: false,
});

/** Stripe can take a moment to finish the subscription after the form says paid. */
const CONFIRM_TRIES = 10;
const CONFIRM_EVERY_MS = 1500;

interface PlanCardProps {
  plan: SubscriptionPlanDto;
  tutorUsername: string;
  loggedIn: boolean;
  isOwner: boolean;
}

/** One tier, bought in the card: Join, the sign-in when needed, then Stripe's form in place. */
export function PlanCard({ plan, tutorUsername, loggedIn, isOwner }: PlanCardProps) {
  const router = useRouter();
  const [stage, setStage] = useState<'idle' | 'auth' | 'opening' | 'paying' | 'confirming'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<{
    sessionId: string;
    clientSecret: string;
    publishableKey: string;
  } | null>(null);

  const open = useCallback(async () => {
    setStage('opening');
    setError(null);
    posthog.capture('subscription_checkout_started', {
      creator_username: tutorUsername,
      provider_username: tutorUsername,
      tutor_username: tutorUsername,
      calls_per_week: plan.callsPerWeek,
      price: plan.price,
    });
    const result = await callAction(() => startSubscription(tutorUsername, plan.id));
    if (!result.success) {
      setStage('idle');
      setError(result.error);
      return;
    }
    const { sessionId, url, clientSecret, publishableKey } = result.data;
    if (clientSecret && publishableKey) {
      setCheckout({ sessionId, clientSecret, publishableKey });
      setStage('paying');
      return;
    }
    if (url) {
      window.location.assign(url);
      return;
    }
    Sentry.captureMessage('Subscription checkout came back empty', { level: 'error' });
    setStage('idle');
    setError('Could not open the payment, try again');
  }, [plan, tutorUsername]);

  const confirm = useCallback(async () => {
    if (!checkout) return;
    setStage('confirming');
    for (let i = 0; i < CONFIRM_TRIES; i++) {
      const result = await callAction(() => confirmSubscription(checkout.sessionId, tutorUsername));
      if (result.success && result.data) {
        posthog.capture('subscription_checkout_completed', {
          creator_username: tutorUsername,
          provider_username: tutorUsername,
          tutor_username: tutorUsername,
          calls_per_week: plan.callsPerWeek,
        });
        router.refresh();
        return;
      }
      await new Promise((r) => setTimeout(r, CONFIRM_EVERY_MS));
    }
    // Paid but not confirmed yet: the webhook will land it, and a reload shows it.
    router.refresh();
  }, [checkout, plan.callsPerWeek, router, tutorUsername]);

  return (
    <div className="rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="sf-title text-title-2 font-extrabold text-[var(--sf-ink)]">
          {planName(plan.callsPerWeek)}
        </span>
        <span className="sf-num shrink-0 text-head-2 font-extrabold text-[var(--sf-ink)]">
          {planTotal(plan)}
        </span>
      </div>
      <span className="sf-note mt-1 block">{planLine(plan)}</span>
      <span className="sf-note block">
        Billed every 4 weeks, fees included. Pause or cancel any time.
      </span>

      {error && <span className="sf-note mt-1 block text-error">{error}</span>}

      {stage === 'auth' && !loggedIn ? (
        <div className="mt-2.5">
          <InlineAuth
            id={`plan-${plan.id}`}
            source="plan_card"
            tutorUsername={tutorUsername}
            tone="card"
            prompt={`Sign in to join ${planName(plan.callsPerWeek).toLowerCase()}`}
            cta="Continue"
            onSuccess={open}
          />
        </div>
      ) : checkout && stage !== 'idle' ? (
        stage === 'confirming' ? (
          <span className="sf-note mt-2.5 block">Paid, setting up your subscription</span>
        ) : (
          <div className="mt-2.5 overflow-hidden rounded-[10px]">
            <EmbeddedCheckoutPanel
              clientSecret={checkout.clientSecret}
              publishableKey={checkout.publishableKey}
              onComplete={() => void confirm()}
            />
          </div>
        )
      ) : (
        <button
          type="button"
          disabled={isOwner || stage === 'opening'}
          onClick={() => (loggedIn ? void open() : setStage('auth'))}
          className="mm-btn sf-cta sf-cta-ink mt-2.5"
        >
          {isOwner ? 'Your plan' : stage === 'opening' ? 'Opening…' : 'Join'}
        </button>
      )}
    </div>
  );
}
